import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaCopy, FaEye, FaEyeSlash, FaExchangeAlt, FaUniversity, FaSyncAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import AuthContext from "../../../context/AuthProvider";
import { getSterlingWallet } from "../../../store/actions/walletActions";
import { SterlingTransferModal } from "./SterlingTransferModal";
import {
  SterlingCard,
  DecorativeRingTop,
  DecorativeRingBottom,
  CardHeader,
  CardBrand,
  CardLogo,
  CardLogoFallback,
  CardBankName,
  CardDots,
  BalanceSection,
  BalanceLabel,
  BalanceRow,
  BalanceAmount,
  CardFooter,
  AccountInfo,
  AccountLabel,
  AccountNumberRow,
  AccountNumber,
  AccountHolder,
  IconToggleButton,
  RefreshButton,
  CopyButton,
  TransferButton,
} from "./style";

export const SterlingWalletCard = ({ onTransferComplete }) => {
  const dispatch = useDispatch();
  const { user } = useContext(AuthContext);
  const sterling = useSelector((state) => state.wallet.sterling);
  const [showBalance, setShowBalance] = useState(true);
  const [showTransfer, setShowTransfer] = useState(false);

  useEffect(() => {
    dispatch(getSterlingWallet(user));
  }, [dispatch, user]);

  const handleRefresh = () => {
    if (sterling.loading) return;
    dispatch(getSterlingWallet(user));
  };

  const handleCopy = async () => {
    if (!sterling.accountNumber) return;
    try {
      await navigator.clipboard.writeText(sterling.accountNumber);
      toast.success("Account number copied");
    } catch {
      toast.error("Could not copy account number");
    }
  };

  const accountDisplay = sterling.loading
    ? "Loading..."
    : sterling.accountNumber || "—";

  return (
    <SterlingCard>
      <DecorativeRingTop />
      <DecorativeRingBottom />

      <CardHeader>
        <CardBrand>
          {sterling.accountImg ? (
            <CardLogo src={sterling.accountImg} alt={sterling.bankName} />
          ) : (
            <CardLogoFallback>
              <FaUniversity />
            </CardLogoFallback>
          )}
          <CardBankName>{sterling.accountName}</CardBankName>
        </CardBrand>
        <CardDots>
          <span /><span /><span />
        </CardDots>
      </CardHeader>

      <BalanceSection>
        <BalanceLabel>Available Balance</BalanceLabel>
        <BalanceRow>
          <BalanceAmount>
            {showBalance
              ? `₦ ${Number(sterling.balance || 0).toLocaleString()}`
              : "₦ ••••••"}
          </BalanceAmount>
          <IconToggleButton
            onClick={() => setShowBalance(!showBalance)}
            aria-label="Toggle balance visibility"
          >
            {showBalance ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
          </IconToggleButton>
          <RefreshButton
            onClick={handleRefresh}
            disabled={sterling.loading}
            $spinning={sterling.loading}
            aria-label="Refresh balance"
          >
            <FaSyncAlt size={13} />
          </RefreshButton>
        </BalanceRow>
      </BalanceSection>

      <CardFooter>
        <AccountInfo>
          <AccountLabel>Account Number</AccountLabel>
          <AccountNumberRow>
            <AccountNumber>{accountDisplay}</AccountNumber>
            {sterling.accountNumber && (
              <CopyButton onClick={handleCopy} aria-label="Copy account number">
                <FaCopy size={11} /> Copy
              </CopyButton>
            )}
          </AccountNumberRow>
          {sterling.bankName && (
            <AccountHolder>{sterling.bankName}</AccountHolder>
          )}
        </AccountInfo>

        <TransferButton onClick={() => setShowTransfer(true)}>
          <FaExchangeAlt size={13} /> Transfer
        </TransferButton>
      </CardFooter>

      <SterlingTransferModal
        show={showTransfer}
        bankTitle={sterling.bankName}
        onClose={() => setShowTransfer(false)}
        balance={sterling.balance}
        onTransferComplete={onTransferComplete}
      />
    </SterlingCard>
  );
};
